import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

const ritualSteps = [
  { id: 1, label: "Tap reset", duration: 1500 },
  { id: 2, label: "Breathe", duration: 2500 },
  { id: 3, label: "Remember", duration: 2000 },
  { id: 4, label: "Continue", duration: 1500 }
];

export const RitualSection = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [userThought, setUserThought] = useState("");

  useEffect(() => {
    if (!isPlaying) return;

    const step = ritualSteps[currentStep];
    if (!step) {
      setTimeout(() => {
        setCurrentStep(0);
        setIsPlaying(false);
        setUserThought("");
      }, 2000);
      return;
    }

    const timer = setTimeout(() => {
      setCurrentStep((prev) => prev + 1);
    }, step.duration);

    return () => clearTimeout(timer);
  }, [currentStep, isPlaying]);

  const startRitual = () => {
    setCurrentStep(0);
    setIsPlaying(true);
    setUserThought("Finish writing that email to Sarah");
  };

  return (
    <section 
      id="ritual"
      data-testid="ritual-section"
      className="py-16 md:py-24 px-6 md:px-12 relative overflow-hidden"
    >
      <div className="section-divider mb-24" />

      <div className="max-w-5xl mx-auto">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
            The flow
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#27272A] mb-6">
            Pause → Remember → Continue
          </h2>
          <p className="text-lg text-[#52525B] max-w-xl mx-auto">
            Watch the ritual in action. This is all it takes to find your thread again.
          </p>
        </motion.div>

        {/* Interactive Demo */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative max-w-md mx-auto"
        >
          {/* Phone mockup - with depth */}
          <div 
            className="bg-white rounded-[3rem] p-3 relative overflow-hidden"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 16px 48px rgba(0, 0, 0, 0.12), 0 32px 64px rgba(0, 0, 0, 0.08)'
            }}
          >
            {/* Phone surface depth */}
            <div 
              className="absolute inset-0 pointer-events-none rounded-[3rem]"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, transparent 30%)' }}
            />
            <div className="bg-[#FDFBF7] rounded-[2.5rem] overflow-hidden aspect-[9/14.4] relative">
              {/* Screen content */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                <AnimatePresence mode="wait">
                  {!isPlaying ? (
                    <motion.div
                      key="initial"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center"
                    >
                      <p className="text-[#0CBCBF] font-semibold text-lg mb-3">
                        Where was I?
                      </p>
                      <motion.button
                        onClick={startRitual}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        data-testid="ritual-start-button"
                        className="w-24 h-24 rounded-full bg-[#F58227] text-white shadow-lg hover:shadow-[#F58227]/30 flex items-center justify-center mb-2 mx-auto"
                      >
                        <RotateCcw size={36} />
                      </motion.button>
                      <p className="text-[#A1A1AA] text-sm mb-2">
                        reset
                      </p>
                      <p className="text-[#A1A1AA] text-sm mb-4">
                        Finding your thread…
                      </p>
                      {/* Four-step flow indicator */}
                      <p className="text-[#D4D4D8] text-xs tracking-wide">
                        Reset → Breathe → Remember → Continue
                      </p>
                    </motion.div>
                  ) : currentStep === 0 ? (
                    <motion.div
                      key="tap"
                      initial={{ scale: 1 }}
                      animate={{ scale: [1, 0.9, 1.1, 1] }}
                      transition={{ duration: 0.5 }}
                      className="text-center"
                    >
                      <div className="w-24 h-24 rounded-full bg-[#F58227] text-white shadow-lg flex items-center justify-center mb-6 mx-auto">
                        <RotateCcw size={36} />
                      </div>
                      <p className="text-[#0CBCBF] font-medium">Resetting...</p>
                    </motion.div>
                  ) : currentStep === 1 ? (
                    <motion.div
                      key="breathe"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center"
                    >
                      <motion.div
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2.5, ease: "easeInOut" }}
                        className="w-32 h-32 rounded-full bg-[#0CBCBF]/20 flex items-center justify-center mb-6 mx-auto"
                      >
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2.5, ease: "easeInOut" }}
                          className="w-20 h-20 rounded-full bg-[#0CBCBF]/40"
                        />
                      </motion.div>
                      <p className="text-[#52525B] font-medium">Take a breath...</p>
                    </motion.div>
                  ) : currentStep === 2 ? (
                    <motion.div
                      key="remember"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="text-center w-full px-4"
                    >
                      <p className="text-[#27272A] font-semibold text-lg mb-4">
                        What were you just doing?
                      </p>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="bg-white rounded-2xl p-4 border-2 border-[#F58227]/30 text-left"
                      >
                        <p className="text-[#27272A]">{userThought}</p>
                        <motion.span
                          animate={{ opacity: [1, 0, 1] }}
                          transition={{ duration: 1, repeat: Infinity }}
                          className="inline-block w-0.5 h-5 bg-[#F58227] ml-1"
                        />
                      </motion.div>
                    </motion.div>
                  ) : currentStep === 3 ? (
                    <motion.div
                      key="continue"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-center"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-16 h-16 rounded-full bg-[#0CBCBF] flex items-center justify-center mb-4 mx-auto"
                      >
                        <span className="text-white text-2xl">✓</span>
                      </motion.div>
                      <p className="font-handwritten text-2xl text-[#F58227] mb-2">
                        "Oh right, that's what I was doing"
                      </p>
                      <p className="text-[#52525B]">Back on track.</p>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="complete"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center"
                    >
                      <p className="text-[#0CBCBF] font-medium mb-4">Reset complete</p>
                      <button
                        onClick={startRitual}
                        className="text-[#F58227] font-medium hover:underline"
                      >
                        Try again
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Progress dots */}
              {isPlaying && (
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {ritualSteps.map((step, i) => (
                    <div
                      key={step.id}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        i <= currentStep ? "bg-[#F58227]" : "bg-stone-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Step labels */}
          <div className="flex justify-center gap-6 mt-8">
            {ritualSteps.map((step, i) => (
              <div
                key={step.id}
                className={`text-center transition-opacity ${
                  isPlaying && i === currentStep ? "opacity-100" : "opacity-40"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 mx-auto ${
                  isPlaying && i <= currentStep ? "bg-[#F58227] text-white" : "bg-stone-200 text-stone-500"
                }`}>
                  {i + 1}
                </div>
                <p className="text-xs font-medium text-[#52525B]">{step.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA below demo - Links to /app */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <Link
            to="/app"
            data-testid="ritual-try-cta"
            className="inline-flex items-center gap-2 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full px-8 py-4 text-lg font-bold transition-all active:scale-95"
            style={{
              boxShadow: '0 2px 8px rgba(245, 130, 39, 0.3), 0 8px 24px rgba(245, 130, 39, 0.2)'
            }}
          >
            Start using the app
          </Link>
          {/* Torn notebook paper quote */}
          <div className="mt-20 -mb-16 flex justify-center">
            <div className="relative max-w-md transform rotate-[-0.5deg]">
              <div
                className="relative"
                style={{
                  background: 'linear-gradient(to bottom, #FEF9E7 0%, #FDF6E3 100%)',
                  padding: '24px 28px 28px 44px',
                  boxShadow: '2px 3px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.08)',
                }}
              >
                {/* Top torn edge */}
                <div 
                  className="absolute -top-[6px] left-0 right-0 h-[12px] overflow-hidden"
                  style={{ background: 'transparent' }}
                >
                  <svg width="100%" height="12" preserveAspectRatio="none" viewBox="0 0 400 12">
                    <path 
                      d="M0,7 Q12,3 24,8 T48,4 T72,7 T96,5 T120,8 T144,4 T168,7 T192,5 T216,8 T240,4 T264,7 T288,5 T312,8 T336,4 T360,7 T384,5 T400,7 L400,12 L0,12 Z" 
                      fill="#FEF9E7"
                    />
                  </svg>
                </div>
                {/* Bottom torn edge */}
                <div 
                  className="absolute -bottom-[6px] left-0 right-0 h-[12px] overflow-hidden"
                  style={{ background: 'transparent' }}
                >
                  <svg width="100%" height="12" preserveAspectRatio="none" viewBox="0 0 400 12">
                    <path 
                      d="M0,0 L400,0 L400,4 Q388,9 376,5 T352,7 T328,4 T304,8 T280,5 T256,7 T232,4 T208,8 T184,5 T160,7 T136,4 T112,8 T88,5 T64,7 T40,4 T16,8 T0,5 Z" 
                      fill="#FDF6E3"
                    />
                  </svg>
                </div>
                {/* Notebook lines */}
                <div 
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 27px, #E8DCCA 27px, #E8DCCA 28px)',
                    backgroundPosition: '0 16px',
                    opacity: 0.5,
                  }}
                />
                {/* Red margin line */}
                <div 
                  className="absolute top-0 bottom-0 left-9 w-[1px] pointer-events-none"
                  style={{ backgroundColor: 'rgba(220, 100, 100, 0.3)' }}
                />
                {/* Text */}
                <p className="font-handwritten text-[1.3rem] md:text-[1.5rem] text-[#4A4540] leading-[1.85] relative z-10">
                  Built by someone who knows what it's like to lose the thread, and often asks...
                  <br />
                  <span className="inline-block mt-1">Uh where was i?</span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
